import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Plus, MapPin, Edit } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import DeleteOrRequestButton from "@/components/DeleteOrRequestButton";

export default function Branches() {
  const { user } = useAuth();
  const isAdmin = user?.role === "crm_manager";
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const calls = [api.get("/branches")];
    if (isAdmin) calls.push(api.get("/users"));
    const [b, u] = await Promise.all(calls);
    setRows(b.data);
    if (u) setUsers(u.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [isAdmin]);

  const bms = users.filter((u) => u.role === "branch_manager" && u.is_active);

  return (
    <div className="p-6 md:p-8 space-y-4" data-testid="branches-page">
      <div className="flex justify-between items-end flex-wrap gap-3">
        <div>
          <div className="mptg-overline">System</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Branches</h1>
          <p className="text-slate-500 mt-1">{rows.length} branch{rows.length === 1 ? "" : "es"} in the network</p>
        </div>
        {isAdmin && (
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
            <DialogTrigger asChild>
              <Button onClick={() => setEditing(null)} className="bg-[#2E86C1] hover:bg-[#2471A3] text-white" data-testid="new-branch-btn">
                <Plus className="w-4 h-4 mr-1" /> New branch
              </Button>
            </DialogTrigger>
            <BranchDialog bms={bms} editing={editing} onDone={() => { setOpen(false); setEditing(null); load(); }} />
          </Dialog>
        )}
      </div>

      <div className="mptg-card p-0 overflow-hidden">
        <table className="w-full mptg-table">
          <thead><tr><th>Code</th><th>Name</th><th>City</th><th>Region</th><th>Branch Manager</th><th className="text-right">Campaigns</th><th>Status</th><th className="text-right">Actions</th></tr></thead>
          <tbody>
            {rows.map((b) => (
              <tr key={b.id}>
                <td className="font-mono text-xs font-semibold">{b.code}</td>
                <td className="font-medium flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-[#2E86C1]" /> {b.name}</td>
                <td className="text-slate-600">{b.city || "-"}</td>
                <td className="text-slate-600">{b.region || "-"}</td>
                <td>{b.branch_manager_name || <span className="text-xs text-slate-400">— unassigned —</span>}</td>
                <td className="text-right tabular-nums">{b.campaign_count || 0}</td>
                <td>{b.status === "active" ? <span className="badge-green rounded px-2 py-0.5 text-xs font-semibold">Active</span> : <span className="badge-slate rounded px-2 py-0.5 text-xs font-semibold">{b.status}</span>}</td>
                <td className="text-right space-x-2 whitespace-nowrap">
                  {isAdmin && (
                    <button onClick={() => { setEditing(b); setOpen(true); }} className="text-[#2E86C1] hover:underline text-xs inline-flex items-center gap-1">
                      <Edit className="w-3.5 h-3.5" /> Edit
                    </button>
                  )}
                  <DeleteOrRequestButton
                    entityType="branch"
                    entity={b}
                    onDeleted={load}
                    onRequested={() => toast.info("Request submitted — visible under Requests")}
                  />
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={8} className="py-8 text-center text-slate-400">No branches yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BranchDialog({ bms, editing, onDone }) {
  const [f, setF] = useState(editing || {
    code: "", name: "", city: "", region: "", branch_manager_id: "", status: "active",
  });
  React.useEffect(() => { if (editing) setF(editing); }, [editing]);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!f.code || !f.name) { toast.error("Code and name required"); return; }
    setSaving(true);
    try {
      const payload = {
        code: f.code, name: f.name, city: f.city, region: f.region,
        branch_manager_id: f.branch_manager_id || null, status: f.status || "active",
      };
      if (editing) await api.patch(`/branches/${editing.id}`, payload);
      else await api.post("/branches", payload);
      toast.success(editing ? "Branch updated" : "Branch created"); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <DialogContent className="max-w-lg">
      <DialogHeader><DialogTitle>{editing ? "Edit branch" : "New branch"}</DialogTitle></DialogHeader>
      <div className="grid grid-cols-2 gap-3 py-2">
        <div><Label>Code *</Label><Input value={f.code || ""} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })} placeholder="MUM" data-testid="branch-code" /></div>
        <div><Label>Name *</Label><Input value={f.name || ""} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Mumbai" data-testid="branch-name" /></div>
        <div><Label>City</Label><Input value={f.city || ""} onChange={(e) => setF({ ...f, city: e.target.value })} /></div>
        <div><Label>Region</Label><Input value={f.region || ""} onChange={(e) => setF({ ...f, region: e.target.value })} placeholder="West" /></div>
        <div className="col-span-2"><Label>Branch Manager</Label>
          <Select value={f.branch_manager_id || "none"} onValueChange={(v) => setF({ ...f, branch_manager_id: v === "none" ? null : v })}>
            <SelectTrigger data-testid="branch-bm-select"><SelectValue placeholder="— unassigned —" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">— unassigned —</SelectItem>
              {bms.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} · {u.email}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div><Label>Status</Label>
          <Select value={f.status || "active"} onValueChange={(v) => setF({ ...f, status: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <DialogFooter>
        <Button onClick={save} disabled={saving} className="bg-[#2E86C1] hover:bg-[#2471A3] text-white" data-testid="save-branch-btn">Save</Button>
      </DialogFooter>
    </DialogContent>
  );
}
